import React, { useCallback, useEffect, useRef, useState } from 'react'

import { CheckCircle, Loader2, XCircle } from 'lucide-react'

import { cn } from '@/utils/cn'

import type { ValueAvailabilityCheckerProps } from './ValueAvailabilityChecker.types'

export const ValueAvailabilityChecker = React.memo<ValueAvailabilityCheckerProps>(
  ({
    value,
    fieldName,
    validateAvailabilityFn,
    onValidationResult,
    zodFieldSchema,
    currentValue,
    disabled = false,
    debounceMs = 800,
    className,
    hasFieldError = false
  }) => {
    const [isChecking, setIsChecking] = useState(false)
    const [isAvailable, setIsAvailable] = useState(false)
    const [hasChecked, setHasChecked] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const mountedRef = useRef(true)

    // Log component lifecycle
    useEffect(() => {
      // console.log(`ValueAvailabilityChecker (${checkType}) mounted with value:`, value)
      mountedRef.current = true

      return () => {
        // console.log(`ValueAvailabilityChecker (${checkType}) unmounting with value:`, value)
        mountedRef.current = false
        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
        }
      }
      // }, [checkType, value])
    }, [fieldName, value])

    // Validation function
    const checkAvailability = useCallback(
      async (valueToCheck: string) => {
        // This function should never be called with empty values now

        // Skip validation if value hasn't changed in edit mode
        if (currentValue && valueToCheck.trim() === currentValue.trim()) {
          setIsChecking(false)
          setIsAvailable(true)
          setHasChecked(true)
          setError(null)
          onValidationResult(true, false)
          return
        }

        // First validate against schema before checking availability
        if (zodFieldSchema) {
          try {
            // Parse the value with the schema to check if it's valid
            zodFieldSchema.parse(valueToCheck)
          } catch (schemaError: unknown) {
            // If schema validation fails, don't check availability
            if (
              typeof schemaError === 'object' &&
              schemaError !== null &&
              'issues' in schemaError &&
              Array.isArray((schemaError as { issues?: unknown }).issues)
            ) {
              console.warn(
                `${fieldName} schema validation failed:`,
                (schemaError as { issues?: { message?: string }[] }).issues?.[0]?.message ||
                  'Invalid format'
              )
            } else {
              console.warn(`${fieldName} schema validation failed:`, 'Invalid format')
            }
            setIsChecking(false)
            setIsAvailable(true) // Set as available since it's not a uniqueness issue
            setHasChecked(false) // Haven't checked uniqueness
            setError(null) // Don't show error here, let form validation handle it
            onValidationResult(true, false) // Report as available to avoid "already in use" message
            return
          }
        }

        // console.log(`Starting ${checkType} availability validation for:`, valueToCheck)
        setIsChecking(true)
        setError(null)
        onValidationResult(false, true)

        try {
          // const validateFn = validateAvailabilityFn
          //   checkType === 'codigo' ? validateCodigoAvailability : validateNombreAvailability

          const available = await validateAvailabilityFn(valueToCheck, currentValue)
          // console.log(`Availability check for ${checkType}:`, { valueToCheck, available })

          if (mountedRef.current) {
            // console.log(`${checkType} validation completed:`, {
            //   available,
            //   mounted: mountedRef.current
            // })
            setIsChecking(false)
            setIsAvailable(available)
            setHasChecked(true)
            onValidationResult(available, false)
          } else {
            console.warn(`${fieldName} validation completed but component unmounted`)
          }
        } catch (err: unknown) {
          if (mountedRef.current) {
            setIsChecking(false)
            setIsAvailable(false)
            setHasChecked(true)
            const errorMessage =
              typeof err === 'object' &&
              err !== null &&
              'message' in err &&
              typeof (err as { message?: unknown }).message === 'string'
                ? (err as { message: string }).message
                : 'Error verificando disponibilidad'
            setError(errorMessage)
            onValidationResult(false, false)
          }
        }
      },
      [fieldName, zodFieldSchema, currentValue, onValidationResult, validateAvailabilityFn]
    )

    // Debounced validation effect
    useEffect(() => {
      if (disabled) return

      // Clear existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      // If value is empty, immediately set as available
      if (!value || value.trim().length === 0) {
        // console.log(`${checkType} field is empty, immediately setting as available`)
        setIsChecking(false)
        setIsAvailable(true)
        setHasChecked(false)
        setError(null)
        onValidationResult(true, false)
        return
      }

      // Set new timeout for non-empty values
      debounceRef.current = setTimeout(() => {
        checkAvailability(value)
      }, debounceMs)

      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
        }
      }
    }, [value, checkAvailability, debounceMs, disabled, fieldName, onValidationResult])

    // Don't render if disabled
    if (disabled) {
      return null
    }

    // Don't render if field has validation error
    if (hasFieldError) {
      return null
    }

    // Don't render if no value (but keep component mounted)
    if (!value || value.trim().length === 0) {
      return null
    }

    const getStatusContent = () => {
      if (isChecking) {
        return {
          icon: <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />,
          text: 'Verificando disponibilidad...',
          className: 'text-blue-600'
        }
      }

      if (error) {
        return {
          icon: <XCircle className="h-3 w-3" aria-hidden="true" />,
          text: error,
          className: 'text-red-600'
        }
      }

      if (hasChecked && isAvailable) {
        // const fieldName = checkType === 'codigo' ? 'Código' : 'Nombre'
        return {
          icon: <CheckCircle className="h-3 w-3" aria-hidden="true" />,
          text: `${fieldName} disponible`,
          className: 'text-green-600'
        }
      }

      if (hasChecked && !isAvailable) {
        // const fieldName = checkType === 'codigo' ? 'código' : 'nombre'
        return {
          icon: <XCircle className="h-3 w-3" aria-hidden="true" />,
          text: `Este ${fieldName} ya está en uso`,
          className: 'text-red-600'
        }
      }

      return null
    }

    const statusContent = getStatusContent()

    if (!statusContent) {
      return null
    }

    return (
      <div
        className={cn('flex items-center gap-1 text-xs', statusContent.className, className)}
        role="status"
        aria-live="polite"
      >
        {statusContent.icon}
        <span>{statusContent.text}</span>
      </div>
    )
  }
)

ValueAvailabilityChecker.displayName = 'ValueAvailabilityChecker'
